import { useEffect, useRef } from 'react';

/**
 * Run a poll on a timer that slows down when nobody is looking at the tab.
 *
 * A dashboard is something people leave open behind other windows for hours,
 * and every one of those hours was costing the same as active use: three
 * separate watchers here each asked the server a question every twenty
 * seconds, forever. For the chat counter that ran into Firestore's daily read
 * allowance and put "Quota exceeded" on the shop's screen; for the others it
 * was simply load nobody was reading.
 *
 * Slower rather than stopped, deliberately. These watchers exist to make a
 * sound when an order or a chat arrives, and a background tab is exactly when
 * that sound matters most — pausing them would have traded a real feature for
 * the saving. A hidden tab still asks, just several times less often, and a
 * tab brought back to the front asks immediately so what it shows is current
 * by the time it is read.
 *
 * @param {() => void | Promise<void>} poll - the request to run. Kept in a ref,
 *   so a caller that rebuilds it every render does not restart the timer.
 * @param {{ visibleMs: number, hiddenMs?: number }} options
 */
export default function useBackgroundPoll(poll, { visibleMs, hiddenMs }) {
  const pollRef = useRef(poll);
  pollRef.current = poll;

  useEffect(() => {
    let timer = null;
    let currentMs = null;

    const run = () => pollRef.current?.();

    const schedule = (ms, { immediate }) => {
      if (currentMs === ms && timer) return;
      if (timer) clearInterval(timer);
      currentMs = ms;
      if (immediate) run();
      timer = setInterval(run, ms);
    };

    const sync = ({ immediate } = { immediate: false }) => {
      const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
      schedule(hidden ? hiddenMs || visibleMs : visibleMs, { immediate: immediate && !hidden });
    };

    // First run happens now whatever the tab is doing: the badge should be
    // right the moment the dashboard loads, including in a tab opened in the
    // background.
    run();
    sync();

    const onVisibility = () => sync({ immediate: true });
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (timer) clearInterval(timer);
    };
  }, [visibleMs, hiddenMs]);
}
